// as a user, I want to select a view, and supply required parameters
// additionally, I want to go back to any main view

// as a developer, I want to declare as little, as possible
// my routes need certain parameters, some of them optional
// I can declare all parameters at will, and I can specify that they resolve to the same page

// there can be multiple routes pointing to the same page
// specifically: details and print are logically the same "page"
// but history and details are different pages
// n routes -> 1 (logical) page
// n views  -> 1 (logical) page
// a back interaction usually wants to go back one logical page

// as implementation, I need to know what view I am in, and in the worst case, restore the context from a route

// a route can (and will) link to multiple views
export type Route<
  Id extends string = string,
  Route extends string = string,
  // biome-ignore lint/suspicious/noExplicitAny: library code is allowed to use any
  Params extends any[] = any[],
  Result extends { id: Id; route: Route; result: string } = { id: Id; route: Route; result: string },
> = {
  id: Id
  route: Route
  make: (...args: Params) => Result
}

// helper type to facilitate [{id: 'a', value: 10}, {id: 'b', value: 20}] -> { a: { id: 'a', value: 10}, b: { id: 'b', value: 20} }
type IndexById<T extends readonly { id: string }[]> = {
  [E in T[number] as E['id']]: E
}
export const combineRoutes = <Routes extends readonly Route[]>(routes: Routes): IndexById<Routes> => {
  return Object.fromEntries(routes.map((e) => [e.id, e])) as IndexById<Routes>
}

export const composeRoute = <
  Id extends string = string,
  Route extends string = string,
  // biome-ignore lint/suspicious/noExplicitAny: library code is allowed to use any
  Params extends any[] = any[],
  Return extends string = string,
>({
  id,
  route,
  make,
}: {
  id: Id
  route: Route
  make: (...args: Params) => Return
}) => {
  return {
    id,
    route,
    make: (...args: Params) => ({ id, route, result: make(...args) }),
  }
}
